import React, { useEffect, useMemo, useRef, useState } from "react";
import { KanbanSquare, Loader2, PlusCircle } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Cache GLOBAL de etapa por contactId, dura 60s.
//
// Evita N+1 repetidos quando a tabela de contatos re-renderiza por paginação
// ou busca. Duas células da mesma linha/navegação não batem na API 2 vezes.
const CACHE_TTL_MS = 60_000;
const CACHE = new Map();
const CRIADO_EM = new Map();

const CARREGANDO = Object.freeze({ status: "loading" });
const VAZIO = Object.freeze({ status: "empty" });

function lerCache(contactId) {
	const id = Number(contactId);
	if (!Number.isFinite(id) || id <= 0) return VAZIO;
	const ttl = CRIADO_EM.get(id);
	if (ttl && Date.now() - ttl > CACHE_TTL_MS) {
		CACHE.delete(id);
		CRIADO_EM.delete(id);
	}
	const v = CACHE.get(id);
	return v === undefined ? null : v;
}

function gravarCache(contactId, valor) {
	const id = Number(contactId);
	if (!Number.isFinite(id) || id <= 0) return;
	CACHE.set(id, valor);
	CRIADO_EM.set(id, Date.now());
}

export function invalidarCacheKanbanContato(contactId) {
	if (contactId === null || contactId === undefined) {
		CACHE.clear();
		CRIADO_EM.clear();
		return;
	}
	const id = Number(contactId);
	CACHE.delete(id);
	CRIADO_EM.delete(id);
}

// Retorna o status do andamento do contato no Kanban.
//
// Retorno:
//   { status: "loading" }
//   { status: "empty" }
//   { status: "ok", principal: KanbanCard, total: number }
//
// O "principal" é o cartão mais recente (backend já ordena por updatedAt DESC).
function usarEtapaKanban(contactId, { enabled = true } = {}) {
	const id = Number(contactId);
	const valido = enabled && Number.isFinite(id) && id > 0;

	const inicial = valido ? lerCache(id) || CARREGANDO : VAZIO;
	const [estado, setEstado] = useState(inicial);
	const abriu = useRef(false);

	useEffect(() => {
		if (!valido) {
			setEstado(VAZIO);
			return undefined;
		}
		const doCache = lerCache(id);
		if (doCache) {
			setEstado(doCache);
			return undefined;
		}
		setEstado(CARREGANDO);
		const ativo = { current: true };
		abriu.current = true;
		api
			.get(`/kanban/cards/by-contact/${id}`)
			.then(({ data }) => {
				if (!ativo.current) return;
				const lista = Array.isArray(data) ? data : [];
				const principal = lista[0] || null;
				const valor = principal
					? Object.freeze({ status: "ok", principal, total: lista.length })
					: VAZIO;
				gravarCache(id, valor);
				setEstado(valor);
			})
			.catch((err) => {
				if (!ativo.current) return;
				gravarCache(id, VAZIO);
				setEstado(VAZIO);
				toastError(err);
			});
		return () => {
			ativo.current = false;
		};
	}, [valido, id]);

	return estado;
}

const NOME_COR = (cor) => {
	const limpa = String(cor || "").trim().replace(/^#/, "");
	if (!limpa) return null;
	return `#${limpa}`;
};

const KanbanEtapaChip = ({
	contactId,
	showTitle = false,
	size = "sm",
	onCriar = null,
	mostrarVazio = true,
}) => {
	const estado = usarEtapaKanban(contactId);
	const classNameTamanho =
		size === "lg"
			? "text-sm px-3 py-1"
			: size === "xs"
				? "text-[10px] px-1.5 py-0.5"
				: "text-xs px-2 py-0.5";

	// useMemo FORA de qualquer early return para obedecer a regra dos Hooks
	// "Rendered same number of hooks every render". Sem isso, quando o status
	// mudava de loading/empty → ok dava erro de hooks.
	const principal = estado.status === "ok" ? estado.principal : null;
	const total = estado.status === "ok" ? estado.total : 0;
	const etapaNome = principal?.kanbanColumn?.name;
	const etapaCor = principal?.kanbanColumn?.color;
	const hex = NOME_COR(etapaCor);
	const estilo = useMemo(() => {
		if (!hex) return undefined;
		return {
			backgroundColor: hex + "1F",
			color: hex,
			borderColor: hex + "55",
			borderWidth: 1,
			borderStyle: "solid",
		};
	}, [hex]);

	if (estado.status === "loading") {
		return (
			<Badge variant="outline" className={`gap-1 text-muted-foreground ${classNameTamanho}`}>
				<Loader2 className="h-3 w-3 animate-spin" />
				<span className="hidden sm:inline">Kanban…</span>
			</Badge>
		);
	}
	if (estado.status === "empty") {
		if (!mostrarVazio) return null;
		if (typeof onCriar === "function") {
			return (
				<Button
					variant="outline"
					size="sm"
					className={`gap-1.5 h-7 font-normal text-muted-foreground ${classNameTamanho}`}
					onClick={(e) => {
						e.stopPropagation && e.stopPropagation();
						onCriar();
					}}
				>
					<PlusCircle className="h-3.5 w-3.5" />
					<span className="hidden sm:inline">Adicionar ao Kanban</span>
					<span className="sm:hidden">Kanban</span>
				</Button>
			);
		}
		return (
			<Badge variant="outline" className={`gap-1 text-muted-foreground/80 ${classNameTamanho}`}>
				<KanbanSquare className="h-3 w-3" />
				<span className="hidden sm:inline">Não no Kanban</span>
			</Badge>
		);
	}

	return (
		<Badge
			variant="outline"
			className={`gap-1.5 font-medium ${classNameTamanho}`}
			title={
				showTitle
					? `Etapa atual: ${etapaNome || "—"}${total > 1 ? ` · +${total - 1} cartão(ões)` : ""}`
					: undefined
			}
			style={estilo}
		>
			<KanbanSquare className="h-3 w-3" />
			<span className="max-w-[140px] truncate">
				{etapaNome || "Etapa"}
			</span>
			{total > 1 && (
				<span className="text-[10px] opacity-70">+{total - 1}</span>
			)}
		</Badge>
	);
};

export default KanbanEtapaChip;
